import { useState, useMemo, useEffect } from "react";
import { DateRange } from "react-day-picker";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useActiveStoreId } from "./campaigns/useActiveStoreId";
import { useCouponDashboardApi } from "./useCouponDashboardApi";
import { formatCurrency } from "@/utils/dashboardUtils";
import type { CouponMetricsData } from "./useCouponDashboardApi";

interface FormattedDateRange {
  fromDate: string;
  toDate: string;
  daysDiff: number;
}

export const useCouponDashboardMetrics = (dateRange: DateRange | undefined) => {
  const [isReady, setIsReady] = useState(false);
  
  // Executa automaticamente quando o componente monta
  useEffect(() => {
    setIsReady(true);
  }, []);
  
  // Format date range
  const formattedDateRange = useMemo<FormattedDateRange>(() => {
    if (!dateRange?.from || !dateRange?.to) {
      const today = new Date();
      const fromDate = format(new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
      const toDate = format(today, 'yyyy-MM-dd');
      return { fromDate, toDate, daysDiff: 7 };
    }

    const fromDate = format(dateRange.from, 'yyyy-MM-dd');
    const toDate = format(dateRange.to, 'yyyy-MM-dd');
    const daysDiff = Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24));

    return { fromDate, toDate, daysDiff };
  }, [dateRange]);

  const { fetchCouponMetrics } = useCouponDashboardApi();
  const { data: storeId, isLoading: isLoadingStoreId } = useActiveStoreId();
  
  // Sempre busca dados quando ready E quando storeId está disponível
  const {
    data: couponMetrics,
    isLoading: isLoadingMetrics,
    error: metricsError
  } = useQuery({
    queryKey: ['coupon-metrics', formattedDateRange.fromDate, formattedDateRange.toDate, storeId],
    queryFn: () => fetchCouponMetrics(formattedDateRange.fromDate, formattedDateRange.toDate),
    enabled: isReady && !!storeId, // Aguarda ter ready E storeId
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2
  });

  const queryClient = useQueryClient();

  // Calculate derived metrics
  const calculatedMetrics = useMemo(() => {
    if (!couponMetrics) {
      return {
        totalRevenue: 0,
        totalCoupons: 0,
        couponsUsed: 0,
        couponsActive: 0,
        couponsPending: 0,
        conversionRate: 0,
        averageTicket: 0,
        couponROI: 0
      };
    }

    const conversionRate = couponMetrics.couponsActive > 0 
      ? Math.round((couponMetrics.couponsUsed / couponMetrics.couponsActive) * 100 * 10) / 10 
      : 0;

    // Ticket médio só faz sentido com cashback utilizados
    const averageTicket = couponMetrics.couponsUsed > 0 
      ? Math.round((couponMetrics.totalRevenue / couponMetrics.couponsUsed) * 100) / 100 
      : 0;

    // ROI só faz sentido se houve cashback utilizados
    const couponROI = couponMetrics.couponsUsed > 0 && couponMetrics.totalCouponValue > 0 
      ? Math.round((couponMetrics.totalRevenue / couponMetrics.totalCouponValue) * 100) / 100 
      : 0;

    return {
      totalRevenue: couponMetrics.totalRevenue,
      totalCoupons: couponMetrics.totalCoupons,
      couponsUsed: couponMetrics.couponsUsed,
      couponsActive: couponMetrics.couponsActive,
      couponsPending: couponMetrics.couponsPending,
      conversionRate,
      averageTicket,
      couponROI
    };
  }, [couponMetrics]);

  // Prefetch strategy for previous period
  useEffect(() => {
    if (formattedDateRange.daysDiff > 3) {
      const previousFromDate = format(
        new Date(new Date(formattedDateRange.fromDate).getTime() - formattedDateRange.daysDiff * 24 * 60 * 60 * 1000),
        'yyyy-MM-dd'
      );
      const previousToDate = format(
        new Date(new Date(formattedDateRange.toDate).getTime() - formattedDateRange.daysDiff * 24 * 60 * 60 * 1000),
        'yyyy-MM-dd'
      );

      queryClient.prefetchQuery({
        queryKey: ['coupon-metrics', previousFromDate, previousToDate],
        queryFn: () => fetchCouponMetrics(previousFromDate, previousToDate),
        staleTime: 5 * 60 * 1000
      });
    }
  }, [formattedDateRange, fetchCouponMetrics, queryClient]);

  return {
    couponMetrics,
    isLoadingMetrics,
    error: metricsError,
    ...calculatedMetrics,
    formatCurrency,
    // Format data for charts
    revenueChartData: couponMetrics?.revenueByDay?.map(item => ({
      ...item,
      date: format(new Date(item.date), 'dd/MM')
    })) || [],
    couponsChartData: couponMetrics?.couponsByDay?.map(item => ({
      ...item,
      date: format(new Date(item.date), 'dd/MM')
    })) || [],
    statusChartData: couponMetrics?.couponsByStatus || []
  };
};